import { useMemo } from 'react'

import { F0Checkbox, F0Text } from '@factorialco/f0-react'
import { Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

// Canonical weekday order. Used both as the render order and as the basis for
// serializing back into the comma-joined string the backend expects, so the
// emitted value is always sorted (e.g. "monday,wednesday,sunday" round-trips
// through any selection without re-shuffling).
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

type Weekday = (typeof WEEKDAYS)[number]

const getWeekdayLabel = (day: Weekday): string => {
  switch (day) {
    case 'monday':
      return i18n.t('hiring.work_days.monday')
    case 'tuesday':
      return i18n.t('hiring.work_days.tuesday')
    case 'wednesday':
      return i18n.t('hiring.work_days.wednesday')
    case 'thursday':
      return i18n.t('hiring.work_days.thursday')
    case 'friday':
      return i18n.t('hiring.work_days.friday')
    case 'saturday':
      return i18n.t('hiring.work_days.saturday')
    case 'sunday':
      return i18n.t('hiring.work_days.sunday')
  }
}

export interface WeekdaysSelectorProps {
  /** Comma-joined weekday string, e.g. "monday,wednesday". */
  value: string
  /** Emits the new selection as a comma-joined string in canonical (Mon–Sun) order. */
  onChange: (next: string) => void
  /** Optional group label rendered above the row. */
  label?: string
  /** Optional inline error message rendered beneath the row. */
  error?: string
  /** Parity prop with other DynamicField inputs; surfaced on each checkbox. */
  required?: boolean
}

const WeekdaysSelector = ({ value, onChange, label, error, required }: WeekdaysSelectorProps) => {
  const selected = useMemo(() => {
    const set = new Set<string>()
    for (const day of value.split(',')) {
      const trimmed = day.trim()
      if (trimmed.length > 0) set.add(trimmed)
    }
    return set
  }, [value])

  const handleToggle = (day: Weekday) => {
    const next = new Set(selected)
    if (next.has(day)) {
      next.delete(day)
    } else {
      next.add(day)
    }
    // Re-serialize in canonical order so the emitted string is stable
    // regardless of the order toggles happened in.
    const ordered = WEEKDAYS.filter((d) => next.has(d))
    onChange(ordered.join(','))
  }

  return (
    <Stack gap="2">
      {label && <F0Text variant="body" content={label} />}
      {/*
        F0's experimental Stack defaults to column even with `inline`; force
        the row direction via inline style so the 7 day checkboxes lay out on
        a single line. Same workaround used in `TicketAttachments.tsx`.
      */}
      <Stack alignItems="center" gap="3" style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {WEEKDAYS.map((day) => (
          <F0Checkbox
            key={day}
            checked={selected.has(day)}
            onCheckedChange={() => handleToggle(day)}
            title={getWeekdayLabel(day)}
            required={required}
          />
        ))}
      </Stack>
      {error && (
        // TODO: replace with a dedicated F0 error style once the design system
        // exposes a helper-text/error slot we can reuse here.
        <F0Text variant="body" content={error} />
      )}
    </Stack>
  )
}

export default WeekdaysSelector
